import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ExecutionCall {
  id: number;
  task: string;
  model: string;
  validation: string;
  rejectReasons: string[];
  tokensIn: number | null;
  tokensOut: number | null;
  tokensThinking: number | null;
  finishReason: string | null;
  latencyMs: number;
  createdAt: string;
}

interface ExecutionLog {
  simulationId: number;
  hasCallRecord: boolean;
  inputs: {
    orders: number | null;
    machines: number | null;
    inProgress: number | null;
    activeRules: number | null;
    promptChars: number | null;
    rules: Array<{ level: number; title: string }>;
    level3: { objetivo: string; plan: string; reglas: string };
  };
  calls: ExecutionCall[];
  validation: { removedByGuardrail: number };
  result: {
    scheduledOrders: number;
    setupSavedMinutes: number | null;
    balanceScore: number | null;
    onTimeRate: number | null;
    recommendations: string[];
    warnings: string[];
  };
  rawResponseVisible: boolean;
  rawResponse: { text: string; totalChars: number; truncated: boolean } | null;
}

const TASK_LABELS: Record<string, string> = {
  OPTIMIZE_LEGACY: 'Optimización completa con IA',
  COMPILE_RULE: 'Compilar regla',
  EXPLAIN: 'Explicar plan',
  INTERPRET_COMMAND: 'Interpretar pedido',
};

const VALIDATION_STYLES: Record<string, { label: string; cls: string }> = {
  ACCEPTED: { label: 'Aceptada', cls: 'bg-green-100 text-green-700' },
  REJECTED: { label: 'Rechazada', cls: 'bg-red-100 text-red-700' },
  ESCALATED: { label: 'Escalada', cls: 'bg-amber-100 text-amber-700' },
};

const num = (v: number | null | undefined) => (v == null ? '—' : v.toLocaleString('es-MX'));
const pct = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(0)}%`);
const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;
const fmtDate = (v: string) => {
  const d = new Date(v);
  return isNaN(d.getTime()) ? v : d.toLocaleString('es-MX');
};

async function readError(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({}));
  return body?.error || fallback;
}

const Step: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
  <section className="border border-gray-200 rounded-xl p-4">
    <h3 className="font-semibold text-gray-800 text-sm mb-3">
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs mr-2">{n}</span>
      {title}
    </h3>
    {children}
  </section>
);

const Stat: React.FC<{ label: string; value: string; warn?: boolean }> = ({ label, value, warn }) => (
  <div className={`rounded-lg px-3 py-2 ${warn ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50 border border-gray-200'}`}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${warn ? 'text-amber-800' : 'text-gray-800'}`}>{value}</div>
  </div>
);

interface Props {
  simulationId: number;
}

const SimulationExecutionPanel: React.FC<Props> = ({ simulationId }) => {
  const [log, setLog] = useState<ExecutionLog | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const load = useCallback(async (id: number) => {
    const req = ++requestRef.current;
    const isCurrent = () => requestRef.current === req;
    setLoading(true);
    setError(null);
    setLog(null);
    try {
      const res = await fetch(`/api/planning/simulations/${id}/execution`, { credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res, 'Error al cargar la ejecución'));
      const data = await res.json();
      if (isCurrent()) setLog(data.execution);
    } catch (e: any) {
      if (isCurrent()) setError(e.message);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(simulationId);
    return () => {
      requestRef.current++;
    };
  }, [simulationId, load]);

  if (loading) return <p className="text-gray-500 text-sm">Cargando ejecución...</p>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>;
  if (!log) return null;

  const l3 = log.inputs.level3;
  const hasL3 = !!(l3.objetivo || l3.plan || l3.reglas);

  return (
    <div className="space-y-4">
      {!log.hasCallRecord && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-sm">
          No hay registro de la llamada al modelo para esta simulación (se creó antes de que se guardara). Se muestran las entradas y el resultado almacenados.
        </div>
      )}

      <Step n={1} title="Entradas de la corrida">
        {log.hasCallRecord && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <Stat label="Órdenes por planear" value={num(log.inputs.orders)} />
            <Stat label="Máquinas activas" value={num(log.inputs.machines)} />
            <Stat label="Órdenes en producción" value={num(log.inputs.inProgress)} />
            <Stat label="Reglas activas" value={num(log.inputs.activeRules)} />
          </div>
        )}
        <h4 className="text-xs font-semibold text-gray-500 mb-1">Reglas N1-N2 vigentes</h4>
        {log.inputs.rules.length > 0 ? (
          <ul className="text-sm space-y-1 mb-3">
            {log.inputs.rules.map((r, i) => (
              <li key={`${r.level}-${r.title}-${i}`}>
                <span className="text-xs font-semibold text-gray-500 mr-2">N{r.level}</span>
                {r.title}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 mb-3">Sin reglas registradas.</p>
        )}
        <h4 className="text-xs font-semibold text-gray-500 mb-1">Contexto del planificador (nivel 3)</h4>
        {hasL3 ? (
          <dl className="text-sm space-y-1">
            {l3.objetivo && <div><dt className="inline font-medium">Objetivo: </dt><dd className="inline text-gray-600 whitespace-pre-wrap">{l3.objetivo}</dd></div>}
            {l3.plan && <div><dt className="inline font-medium">Plan: </dt><dd className="inline text-gray-600 whitespace-pre-wrap">{l3.plan}</dd></div>}
            {l3.reglas && <div><dt className="inline font-medium">Reglas: </dt><dd className="inline text-gray-600 whitespace-pre-wrap">{l3.reglas}</dd></div>}
          </dl>
        ) : (
          <p className="text-sm text-gray-500">No se escribió contexto adicional.</p>
        )}
      </Step>

      <Step n={2} title="Llamada al modelo">
        {log.calls.length === 0 ? (
          <p className="text-sm text-gray-500">Sin registro de la llamada.</p>
        ) : (
          <div className="space-y-3">
            {log.calls.map((c) => {
              const style = VALIDATION_STYLES[c.validation] ?? { label: c.validation, cls: 'bg-gray-100 text-gray-700' };
              const abnormalFinish = c.finishReason != null && c.finishReason !== 'STOP';
              return (
                <div key={c.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center flex-wrap gap-2 text-sm mb-2">
                    <span className="font-medium">{TASK_LABELS[c.task] ?? c.task}</span>
                    <span className="text-gray-500">· {c.model}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${style.cls}`}>{style.label}</span>
                    <span className="text-xs text-gray-500">{fmtDate(c.createdAt)}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <Stat label="Tokens de entrada" value={num(c.tokensIn)} />
                    <Stat label="Tokens de razonamiento" value={num(c.tokensThinking)} />
                    <Stat label="Tokens de salida" value={num(c.tokensOut)} />
                    <Stat label="Motivo de fin" value={c.finishReason ?? '—'} warn={abnormalFinish} />
                    <Stat label="Duración" value={seconds(c.latencyMs)} />
                  </div>
                  {abnormalFinish && (
                    <p className="text-xs text-amber-800 mt-2">
                      El modelo no terminó de forma normal ({c.finishReason}); la respuesta pudo quedar incompleta.
                    </p>
                  )}
                  {c.rejectReasons.length > 0 && (
                    <ul className="list-disc list-inside text-xs text-red-700 mt-2">
                      {c.rejectReasons.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Step>

      <Step n={3} title="Validación del sistema">
        {log.validation.removedByGuardrail > 0 ? (
          <p className="text-sm text-amber-800">
            El sistema descartó {log.validation.removedByGuardrail} asignación(es) a máquinas que no existen en el catálogo.
          </p>
        ) : (
          <p className="text-sm text-gray-600">No se descartó ninguna asignación por máquinas inexistentes.</p>
        )}
      </Step>

      <Step n={4} title="Resultado">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <Stat label="Órdenes programadas" value={num(log.result.scheduledOrders)} />
          <Stat label="Setup ahorrado (min)" value={num(log.result.setupSavedMinutes)} />
          <Stat label="Balance de carga" value={pct(log.result.balanceScore)} />
          <Stat label="Entrega a tiempo" value={pct(log.result.onTimeRate)} />
        </div>
        <p className="text-xs text-gray-500">
          Estas métricas las calculó el modelo y se guardan tal cual; el sistema solo las limita a 0-100%. Todavía no hay una verificación independiente.
        </p>
        {log.result.warnings.length > 0 && (
          <ul className="list-disc list-inside text-sm text-amber-900 mt-2 space-y-0.5">
            {log.result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        )}
      </Step>

      <Step n={5} title="Respuesta cruda del modelo">
        {log.rawResponseVisible ? (
          log.rawResponse ? (
            <details>
              <summary className="cursor-pointer text-sm text-purple-700">
                Ver texto ({log.rawResponse.totalChars.toLocaleString('es-MX')} caracteres)
              </summary>
              <pre className="mt-2 bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-auto max-h-96 whitespace-pre-wrap break-words">{log.rawResponse.text}</pre>
              {log.rawResponse.truncated && (
                <p className="text-xs text-gray-500 mt-1">Se muestra solo el inicio del texto.</p>
              )}
            </details>
          ) : (
            <p className="text-sm text-gray-500">No hay texto guardado.</p>
          )
        ) : (
          <p className="text-sm text-gray-500">El texto crudo solo lo ven usuarios con permiso de configuración.</p>
        )}
        <p className="text-xs text-gray-500 mt-3">
          Hoy el modelo no explica por qué eligió cada máquina, así que no hay un detalle por orden. Ese detalle llegará con el motor determinista.
        </p>
      </Step>
    </div>
  );
};

export default SimulationExecutionPanel;
